import React, { useState, useEffect, useCallback } from 'react';
import DoctorList from './calendar/DoctorList.jsx';
import Calendar from './calendar/Calendar.jsx';
import Todos from './todos/Todos.jsx';
import { getInitialInfo } from './../logic/calendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { sortBy, camel } from './../libs/general';

function Main() {
	const [state, setState] = useState({
		alphabeticalList: [],
		favouritesList: [],
		todosList: [],
		currentView: 'calendar',
		isSidebarOpen: true
	});

	const isSmallScreen = () => {
		return Math.max(document.documentElement.clientWidth, window.innerWidth || 0) < 770 ? false : true;
	};

	useEffect(() => {
		async function loadInitialData() {
			const isSidebarOpen = isSmallScreen();
			
			// Check for the special first load case
			if (!window.localStorage.getItem('firstLoad')) {
				window.localStorage.setItem('firstLoad', "false");
				document.location.reload();
			}
			
			// Clear doctor ID if it matches the user ID
			// This prevents the confusion between user ID and doctor ID
			const userId = window.localStorage.getItem('userId');
			const currentDoctorId = window.localStorage.getItem('currentDoctorId');
			
			if (userId && currentDoctorId && userId === currentDoctorId) {
				console.log('Detected userId incorrectly used as doctorId - clearing currentDoctorId');
				window.localStorage.removeItem('currentDoctorId');
			}

			try {
				const initialInfo = await getInitialInfo();

				// Add null/undefined checks before accessing properties
				const updatedState = { ...state, isSidebarOpen };
				
				if (initialInfo) {
					// Check and set favouritesList if it exists
					if (initialInfo.favouritesList) {
						updatedState.favouritesList = initialInfo.favouritesList.sort(sortBy("name.lastName"));
					} else {
						updatedState.favouritesList = [];
						console.warn("favouritesList not found in initialInfo", initialInfo);
					}
					
					// Check and set alphabeticalList if it exists
					if (initialInfo.alphabeticalList) {
						updatedState.alphabeticalList = initialInfo.alphabeticalList.sort(sortBy("name.lastName"));
					} else {
						updatedState.alphabeticalList = [];
						console.warn("alphabeticalList not found in initialInfo", initialInfo);
					}
					
					// Check and set todosList if it exists
					updatedState.todosList = initialInfo.todosList || [];
				} else {
					console.warn("initialInfo is null or undefined");
					updatedState.favouritesList = [];
					updatedState.alphabeticalList = [];
					updatedState.todosList = [];
				}

				setState(updatedState);
			} catch (e) {
				console.log(e);
				// Set default values in case of error
				setState({
					...state,
					alphabeticalList: [],
					favouritesList: [],
					todosList: [],
					isSidebarOpen
				});
			}
		}
		
		loadInitialData();
	}, []);

	const handleTodoDelete = (todoId) => {
		setState(prevState => ({
			...prevState,
			todosList: prevState.todosList.filter((todo) => todo.todoId !== todoId)
		}));
	};

	// Memoize handlers to minimize rerenders
	const refresh = useCallback(() => {
			// Clear doctor ID cache to force a reload
			window.localStorage.removeItem('myDoctorId');
			window.localStorage.removeItem('myRetrievePast');
			
			console.log('Main.jsx: Refresh triggered, clearing cache and updating refreshCounter');
			
			// Use a functional state update to ensure we're not causing unnecessary rerenders
			setState(prevState => {
					return { ...prevState, refreshCounter: (prevState.refreshCounter || 0) + 1 };
			});
	}, []);

	const showTasksView = () => {
		setState(prevState => ({ ...prevState, currentView: 'tasks' }));
		closeSidebar();
	};

	const showCalendarView = () => {
		setState(prevState => ({ ...prevState, currentView: 'calendar' }));
		closeSidebar();
	};

	const toggleSidebar = () => {
		setState(prevState => ({ ...prevState, isSidebarOpen: !prevState.isSidebarOpen }));
	};

	const closeSidebar = () => {
		if (isSmallScreen()) {
			setState(prevState => ({ ...prevState, isSidebarOpen: false }));
		}
	};

	const renderAlphabeticalList = () => {
		return <DoctorList closeSidebar={closeSidebar} refresh={refresh} showCalendarView={showCalendarView} list={state.alphabeticalList} />
	};

	const renderFavouritesList = () => {
		return <DoctorList closeSidebar={closeSidebar} refresh={refresh} showCalendarView={showCalendarView} list={state.favouritesList} />
	};

	const renderTodosList = () => {
		return <Todos closeSidebar={closeSidebar} handleTodoDelete={handleTodoDelete} list={state.todosList} hidden={state.currentView === 'tasks' ? false : true} />
	};

	const renderCalendar = () => {
		return <Calendar closeSidebar={closeSidebar} refresh={refresh} calendarOrTasks={state.currentView} hidden={state.currentView === 'calendar' ? false : true} />
	};

	return (
		<div> {/* whole page */}
			<Row>
				<Col xs={1} style={{ marginLeft: "20px" }}>
					<FontAwesomeIcon className="calendar__sidebar--toggle" icon="bars" onClick={toggleSidebar} />
				</Col>
				<Col xs={8} className="offset-xs-1">
					{
						window.localStorage.getItem('currentDoctorId') ?
							<p className="calendar__doctor-title--text"><span>{camel(window.localStorage.getItem("currentDoctorTitle"))}.&nbsp;{camel(window.localStorage.getItem("currentDoctorLastName"))},&nbsp;{camel(window.localStorage.getItem("currentDoctorFirstName"))}</span></p>
							: false
					}
				</Col>
			</Row>
				<Row className="flex-nowrap">
				{
					state.isSidebarOpen ?
						<Col xs={12} md={2} lg={2} style={{ zIndex: "999" }} className="sidebar-col">
							<div className="calendar__doctor-lists--container noprint"> {/* doctor lists */}
								<h4>Favourites</h4>
								<div className="calendar__doctor-lists--list"> {/* favourites */}
									{renderFavouritesList()}
								</div>
								<hr></hr>
								<h4>Alphabetical</h4>
								<div className="calendar__doctor-lists--list"> {/* alphabetical */}
									{renderAlphabeticalList()}
								</div>
							</div>
						</Col>
						: false
				}
				<Col xs={12} md={state.isSidebarOpen ? 10 : 12} lg={state.isSidebarOpen ? 10 : 12} hidden={state.currentView !== 'tasks'} className="content-col">
					{renderTodosList()}
				</Col>
				<Col xs={12} md={state.isSidebarOpen ? 10 : 12} lg={state.isSidebarOpen ? 10 : 12} hidden={state.currentView !== 'calendar'} className="content-col">
					{renderCalendar()}
				</Col>
			</Row>

			<div className="tiny-push"></div>
			<div style={{ zIndex: "999" }} className="pages__action-bar--container noprint">
				<div className="calendar__view-nav--buttons">
					{
						(window.localStorage.getItem('userRights') === "admin" || window.localStorage.getItem("userRights") === "limitedadmin") ?
							<button onClick={showTasksView} className="my-button">Tasks
								{
									state.todosList && state.todosList instanceof Array && state.todosList.length !== 0 ?
										<p id="todos-number" style={state.todosList.length ? { display: false } : { display: 'none' }}>{state.todosList.length}</p>
										: undefined
								}
							</button>
							: null
					}
					<button onClick={showCalendarView} className="my-button">Calendar</button>
				</div>
				{
					window.localStorage.getItem('userRights') === "admin" || window.localStorage.getItem('userRights') === "limitedadmin" ?
						<div>
							<Link className="pages__action-bar--item neutral-button" to="/settings">
								<FontAwesomeIcon icon="cog" />
							</Link>
							<div className="tiny-push"></div>
						</div>

						: null
				}
			</div>
			<div className="medium-push"></div>
		</div >
	);
}

export default Main;
